import * as React from "react";
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import { IData as OutreachData } from "../../../store/outreach/OutreachItem";
import { CardActions, CardContent, CardHeader, Grid, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { Card } from "react-bootstrap";
import { OutreachIcon } from "../../atoms/icons/OutreachIcon";
import { MedewerkersMultiSelect } from "../../molecules/forms/MedewerkersMultiSelect";
import { ContactsInput } from "../../molecules/forms/ContactsInput";
import { ContactDateSelect } from "../../molecules/forms/ContactDateSelect";
import { OrganisationTextBox } from "../../molecules/forms/OrganisationTextBox";
import { IData as Doelgroep } from "../../../store/outreach/OutreachDoelgroepen";
import { DoelgroepenSelect } from "../../molecules/forms/DoelgroepenSelect";
import { ThemaTextBox } from "../../molecules/forms/ThemaTextBox";
import { AanwezigenInput, AanwezigenType } from "../../molecules/forms/AanwezigenInput";
import { useTranslation } from 'react-i18next';
import { Aanwezigen, AanwezigenType as AanwezigenTypeView } from "../../molecules/views/Aanwezigen";
import { IFormOrganism } from "../IFormOrganism";
import { DoelgroepDetail } from "../../molecules/forms/DoelgroepDetail";


// All the incoming values
interface IProps {
    minDate: Date;
    maxDate: Date;
    minContacts: number;
    maxContacts: number;
    doelgroepen: Doelgroep[];
}

// Outgoing functions
interface IDispatch {
    onDirtyChange?: () => void;
}

interface IValidationResult {
    medewerkers: boolean
    contacts: boolean
    contactDate: boolean
    organisatie: boolean
    doelgroepen: boolean
    doelgroepDetail: boolean
    thema: boolean
    aanwezigenProfessioneel: boolean
    aanwezigenNietProfessioneel: boolean
}

// Actual Component
export type AllProps = IProps & IDispatch & IFormOrganism<OutreachData>;
export const OutreachForm = (props: AllProps) => {
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tOutreach } = useTranslation('outreach');
    const [data, setData] = React.useState(props.data);
    const defaultValidationResult = !props.prevalidate;
    const [validationResult, setValidationResult] = React.useState<IValidationResult>({
        medewerkers: defaultValidationResult,
        contacts: defaultValidationResult,
        contactDate: defaultValidationResult,
        organisatie: defaultValidationResult,
        doelgroepen: defaultValidationResult,
        doelgroepDetail: defaultValidationResult,
        thema: defaultValidationResult,
        aanwezigenProfessioneel: defaultValidationResult,
        aanwezigenNietProfessioneel: defaultValidationResult
    });
    const loading = props.loading;

    // Sync local data state with props.data when it changes
    React.useEffect(() => {
        setData(props.data);
    }, [props.data]);

    const update = (data: Partial<OutreachData>, vr: Partial<IValidationResult>) => {
        setData(prev => {
            const newData = { ...prev, ...data };

            setValidationResult(prevV => {
                const newValidationResult = { ...prevV, ...vr };
                const result = Object.values(newValidationResult).every(v => v === true);
                props.onUpdate && props.onUpdate(newData, result);
                return newValidationResult;
            })

            return newData;
        });
        props.onDirtyChange?.();
    }

    return <Card>
            {props.loading ? <CardHeader title={<Skeleton variant="text" />} subheader={<Skeleton variant="text" />} avatar={<Skeleton variant="circle" />} /> : <CardHeader title={tOutreach('detailTitle', data)} subheader={data.datum.toLocaleDateString()} avatar={<OutreachIcon />} /> }
            <CardContent>
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">


                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tOutreach('detailDopColumn1')}
                    </Typography>
                    <MedewerkersMultiSelect 
                        value={data.medewerkers.map(m => ({ id: m.medewerkerId, fullName: m.medewerker, streamId: m.medewerkerId }))} 
                        onChange={(medewerkers, isValid) => update({medewerkers: medewerkers?.map(m => ({ medewerkerId: m.streamId, medewerker: m.fullName })) || []}, { medewerkers: isValid })}
                        loading={loading}
                        required
                        prevalidate={props.prevalidate}
                        syncWithRedux={true}
                        showOnlyActive={true}
                    />
                    <ContactsInput 
                        label={tDatatypes('contactenLabelPerMedewerker')} 
                        minValue={props.minContacts}
                        maxValue={props.maxContacts}
                        value={data.contacts}
                        onChange={(value, isValid) => update({ contacts: value ?? 0 }, { contacts: isValid } )}
                        loading={loading}
                        required
                        prevalidate={props.prevalidate}
                    />
                    <ContactDateSelect
                        minValue={props.minDate}
                        maxValue={props.maxDate}
                        value={data.datum}
                        onChange={(date, isValid) => update({ datum: date || data.datum }, { contactDate: isValid })} 
                        loading={loading}
                        required
                        prevalidate={props.prevalidate}
                    />
                </Grid>


                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tOutreach('detailDopColumn2')}
                    </Typography>
                    <OrganisationTextBox
                        value={data.organisatie}
                        onChange={(value, isValid) => update({ organisatie: value ?? "" }, { organisatie: isValid })}
                        required
                        prevalidate={props.prevalidate}
                        loading={loading}
                    />

                    <DoelgroepenSelect

                        options={props.doelgroepen}
                        value={data.doelgroepen.map(d => ({ code: d.doelgroepId, naam: d.doelgroep, descriptionEnabled: false }))}
                        onChange={(doelgroepen, isValid) => update({ doelgroepen: doelgroepen?.map(d => ({ doelgroepId: d.code, doelgroep: d.naam })) || [] }, { doelgroepen: isValid })}
                        loading={loading}
                        required
                        prevalidate={props.prevalidate}

                    />

                    {data.doelgroepen.some(d => props.doelgroepen.find(doelgr => doelgr.code === d.doelgroepId)?.descriptionEnabled) && (
                        <DoelgroepDetail
                            value={data.doelgroepDetail}
                            onChange={(value, isValid) => update({ doelgroepDetail: value ?? "" }, { doelgroepDetail: isValid })}
                            loading={loading}
                            prevalidate={props.prevalidate}
                        />
                    )}

                    <ThemaTextBox

                        value={data.thema}
                        onChange={(value, isValid) => update({ thema: value ?? "" }, { thema: isValid })}
                        required
                        loading={loading}
                        prevalidate={props.prevalidate}

                    />
                </Grid>


                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tOutreach('detailDopColumn3')}
                    </Typography>

                    <AanwezigenInput 
                        AanwezigenType={AanwezigenType.Professioneel}
                        value={data.aantalAanwezigProfessioneel}
                        onChange={(value, isValid) => update({ aantalAanwezigProfessioneel: value || 0 }, { aanwezigenProfessioneel: isValid })}
                        minValue={0}
                        loading={loading}
                        prevalidate={props.prevalidate}
                    />

                    <AanwezigenInput 
                        AanwezigenType={AanwezigenType.NietProfessioneel}
                        value={data.aantalAanwezigNietProfessioneel}
                        onChange={(value, isValid) => update({  aantalAanwezigNietProfessioneel: value || 0 }, {  aanwezigenNietProfessioneel: isValid })}
                        minValue={0}
                        loading={loading}
                        prevalidate={props.prevalidate}
                    />

                    <Aanwezigen
                        type={AanwezigenTypeView.Default}
                        value={data.aantalAanwezigProfessioneel + data.aantalAanwezigNietProfessioneel}
                        loading={loading}
                    />


                </Grid>


            </Grid>
        </CardContent>
        <CardActions>
        
        </CardActions>
        </Card>
}

// CSS
const useStyles = makeStyles(() => ({ }));
